import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import {
  TrashSimpleIcon,
  PlusIcon,
  ClockIcon,
  GlobeHemisphereWestIcon,
  PhoneIcon,
} from '@phosphor-icons/react';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import MessagePopup from '../../../common/MessagePopup';
import { useAlert } from '../../../../hooks/useAlert';
import { useTimeZones } from '../../../../hooks/UseTimeZones';
import { useDashboard } from '../../../../hooks/useDashboard';
import DeleteModal from '../../../modals/DeleteModal';
import theme from '../../../../theme';
import { commonStyles } from '../../../../utils/styles/inputs';
import {
  saveCallForwardingRulesApi,
  getCallForwardingRulesApi,
} from '../../../../utils/apis';
import CallForwardingRulesSkeleton from './CallForwardingRulesSkeleton';

// Types
interface ForwardingRule {
  id: string;
  enabled: boolean;
  forwardToNumber: string;
  timezone: string;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  activeDays: string[];
  secondaryForwarding: {
    enabled: boolean;
    backupNumber: string;
    timeoutSeconds: number;
    conditions: {
      noAnswer: boolean;
      busy: boolean;
      callFailed: boolean;
    };
  };
}

interface CallForwardingConfig {
  defaultFallbackNumber: string;
  rules: ForwardingRule[];
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const WEEKEND = ['Sat', 'Sun'];

// Styles
const commonSelectStyles = {
  height: '40px',
  '& .MuiSelect-select': {
    display: 'flex',
    padding: '8px 14px',
    alignItems: 'center',
    borderRadius: '6px',
    border: '1px solid #DCE0E7',
    background: '#F8F9FC',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 'normal',
    letterSpacing: '-0.14px',
    color: theme.palette.lightGray.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
};

const switchStyles = {
  width: 36,
  height: 20,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: '2px',
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#6366F1',
        opacity: 1,
        border: 0,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#6366F1',
      border: '6px solid #fff',
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 16,
    height: 16,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
  },
};

const CallForwardingRules: FC = () => {
  const { alert, setAlert, showAlert } = useAlert();
  const { timeZones } = useTimeZones();
  const { selectedAssistantId } = useDashboard();
  const queryClient = useQueryClient();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);

  // State
  const [config, setConfig] = useState<CallForwardingConfig>({
    defaultFallbackNumber: '',
    rules: [],
  });

  // Fetch existing rules
  const { data: rulesData, isLoading } = useQuery({
    queryKey: ['call-forwarding-rules', selectedAssistantId],
    queryFn: () => getCallForwardingRulesApi(selectedAssistantId || ''),
    enabled: !!selectedAssistantId,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
  });

  // Load rules from API response
  useEffect(() => {
    if (rulesData?.data) {
      const apiData = rulesData.data;
      setConfig({
        defaultFallbackNumber: apiData.defaultFallbackNumber || '',
        rules: (apiData.rules || []).map((rule: any) => ({
          id: rule._id || `rule-${Date.now()}-${Math.random()}`,
          enabled: rule.enabled ?? true,
          forwardToNumber: rule.forwardToNumber || '',
          timezone: rule.timezone || 'Asia/Kolkata',
          startTime: rule.startTime ? dayjs(rule.startTime, 'HH:mm') : null,
          endTime: rule.endTime ? dayjs(rule.endTime, 'HH:mm') : null,
          activeDays: rule.activeDays || [],
          secondaryForwarding: {
            enabled: rule.secondaryForwarding?.enabled ?? false,
            backupNumber: rule.secondaryForwarding?.backupNumber || '',
            timeoutSeconds: rule.secondaryForwarding?.timeoutSeconds || 30,
            conditions: {
              noAnswer: rule.secondaryForwarding?.conditions?.noAnswer ?? true,
              busy: rule.secondaryForwarding?.conditions?.busy ?? false,
              callFailed:
                rule.secondaryForwarding?.conditions?.callFailed ?? false,
            },
          },
        })),
      });
    }
  }, [rulesData]);

  // Save mutation
  const { mutate: saveRules, isPending: isSaving } = useMutation({
    mutationFn: saveCallForwardingRulesApi,
    onSuccess: () => {
      showAlert('Call forwarding rules saved successfully', 'success');
      queryClient.invalidateQueries({
        queryKey: ['call-forwarding-rules', selectedAssistantId],
      });
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message ||
          'Failed to save call forwarding rules',
        'error'
      );
    },
  });

  // Handlers
  const handleDefaultNumberChange = (phone: string) => {
    setConfig(prev => ({ ...prev, defaultFallbackNumber: phone }));
  };

  const handleAddRule = () => {
    const newRule: ForwardingRule = {
      id: `rule-${Date.now()}`,
      enabled: true,
      forwardToNumber: '',
      timezone: 'Asia/Kolkata',
      startTime: null,
      endTime: null,
      activeDays: WEEKDAYS,
      secondaryForwarding: {
        enabled: false,
        backupNumber: '',
        timeoutSeconds: 30,
        conditions: {
          noAnswer: true,
          busy: false,
          callFailed: false,
        },
      },
    };
    setConfig(prev => ({ ...prev, rules: [...prev.rules, newRule] }));
  };

  const handleDeleteRule = (ruleId: string) => {
    setRuleToDelete(ruleId);
    setDeleteModalOpen(true);
  };

  const confirmDeleteRule = () => {
    if (ruleToDelete) {
      setConfig(prev => ({
        ...prev,
        rules: prev.rules.filter(rule => rule.id !== ruleToDelete),
      }));
      setRuleToDelete(null);
      setDeleteModalOpen(false);
      showAlert('Rule deleted successfully', 'success');
    }
  };

  const handleRuleChange = (
    ruleId: string,
    field: keyof ForwardingRule,
    value: any
  ) => {
    setConfig(prev => ({
      ...prev,
      rules: prev.rules.map(rule =>
        rule.id === ruleId ? { ...rule, [field]: value } : rule
      ),
    }));
  };

  const handleSecondaryForwardingChange = (
    ruleId: string,
    field: string,
    value: any
  ) => {
    setConfig(prev => ({
      ...prev,
      rules: prev.rules.map(rule =>
        rule.id === ruleId
          ? {
              ...rule,
              secondaryForwarding: {
                ...rule.secondaryForwarding,
                [field]: value,
              },
            }
          : rule
      ),
    }));
  };

  const handleConditionChange = (
    ruleId: string,
    condition: 'noAnswer' | 'busy' | 'callFailed',
    value: boolean
  ) => {
    setConfig(prev => ({
      ...prev,
      rules: prev.rules.map(rule =>
        rule.id === ruleId
          ? {
              ...rule,
              secondaryForwarding: {
                ...rule.secondaryForwarding,
                conditions: {
                  ...rule.secondaryForwarding.conditions,
                  [condition]: value,
                },
              },
            }
          : rule
      ),
    }));
  };

  const handleDayToggle = (ruleId: string, day: string) => {
    setConfig(prev => ({
      ...prev,
      rules: prev.rules.map(rule => {
        if (rule.id === ruleId) {
          const isActive = rule.activeDays.includes(day);
          return {
            ...rule,
            activeDays: isActive
              ? rule.activeDays.filter(d => d !== day)
              : [...rule.activeDays, day],
          };
        }
        return rule;
      }),
    }));
  };

  const handleQuickSelectDays = (
    ruleId: string,
    selection: 'all' | 'weekdays' | 'weekend'
  ) => {
    setConfig(prev => ({
      ...prev,
      rules: prev.rules.map(rule => {
        if (rule.id === ruleId) {
          let newDays: string[] = [];
          switch (selection) {
            case 'all':
              newDays = [...DAYS];
              break;
            case 'weekdays':
              newDays = [...WEEKDAYS];
              break;
            case 'weekend':
              newDays = [...WEEKEND];
              break;
          }
          return { ...rule, activeDays: newDays };
        }
        return rule;
      }),
    }));
  };

  const handleSave = () => {
    // Validation
    if (!selectedAssistantId) {
      showAlert('No agent selected', 'error');
      return;
    }

    if (!config.defaultFallbackNumber) {
      showAlert('Please enter a default fallback number', 'error');
      return;
    }

    for (const rule of config.rules) {
      if (!rule.forwardToNumber) {
        showAlert('Please enter a forward to number for all rules', 'error');
        return;
      }
      if (!rule.startTime || !rule.endTime) {
        showAlert('Please set start and end times for all rules', 'error');
        return;
      }
      if (
        rule.startTime.isAfter(rule.endTime) ||
        rule.startTime.isSame(rule.endTime)
      ) {
        showAlert('Start time must be before end time', 'error');
        return;
      }
      if (rule.activeDays.length === 0) {
        showAlert(
          'Please select at least one active day for all rules',
          'error'
        );
        return;
      }
      if (
        rule.secondaryForwarding.enabled &&
        !rule.secondaryForwarding.backupNumber
      ) {
        showAlert('Please enter a backup number for failover', 'error');
        return;
      }
    }

    // Prepare data for API
    const apiData = {
      agentId: selectedAssistantId,
      defaultFallbackNumber: config.defaultFallbackNumber,
      rules: config.rules.map(rule => ({
        enabled: rule.enabled,
        forwardToNumber: rule.forwardToNumber,
        timezone: rule.timezone,
        startTime: rule.startTime?.format('HH:mm') || '',
        endTime: rule.endTime?.format('HH:mm') || '',
        activeDays: rule.activeDays,
        secondaryForwarding: {
          enabled: rule.secondaryForwarding.enabled,
          backupNumber: rule.secondaryForwarding.backupNumber,
          timeoutSeconds: rule.secondaryForwarding.timeoutSeconds,
          conditions: {
            noAnswer: rule.secondaryForwarding.conditions.noAnswer,
            busy: rule.secondaryForwarding.conditions.busy,
            callFailed: rule.secondaryForwarding.conditions.callFailed,
          },
        },
      })),
    };

    saveRules(apiData);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {!selectedAssistantId ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: '16px',
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              Please select an agent to configure call forwarding rules
            </Typography>
          </Box>
        ) : isLoading ? (
          <CallForwardingRulesSkeleton />
        ) : (
          <Stack spacing={3}>
            {/* Header */}
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: 'custom.textBlue',
                  mb: 0.5,
                }}
              >
                Call Forwarding Rules
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: 'text.secondary',
                }}
              >
                Configure time-based call forwarding with timezone support
              </Typography>
            </Box>

            <Divider />

            {/* Default Fallback Number */}
            <Box
              sx={{
                backgroundColor: '#F8F9FC',
                padding: 2,
                borderRadius: '8px',
                border: '1px solid #E0E0E0',
              }}
            >
              <FormControl fullWidth>
                <FormLabel sx={{ ...commonStyles.formLabel, mb: 1 }}>
                  Default Fallback Number
                </FormLabel>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: 'text.secondary',
                    mb: 1.5,
                  }}
                >
                  Calls will be forwarded to this number if no time slot matches
                </Typography>
                <PhoneInput
                  country="in"
                  value={config.defaultFallbackNumber}
                  onChange={handleDefaultNumberChange}
                  inputStyle={{
                    width: '100%',
                    height: '40px',
                    fontSize: '14px',
                  }}
                  specialLabel=""
                  containerStyle={{ flex: 1 }}
                />
              </FormControl>
            </Box>

            {/* Rules */}
            {config.rules.map((rule, index) => (
              <Box
                key={rule.id}
                sx={{
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  padding: 2,
                  backgroundColor: '#FFFFFF',
                }}
              >
                {/* Rule Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'custom.textBlue',
                      }}
                    >
                      Rule {index + 1}
                    </Typography>
                    <Switch
                      checked={rule.enabled}
                      onChange={e =>
                        handleRuleChange(rule.id, 'enabled', e.target.checked)
                      }
                      color="primary"
                      size="small"
                      sx={switchStyles}
                    />
                  </Box>
                  <IconButton
                    sx={{
                      backgroundColor: '#E74C3C26',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: '#E74C3C40',
                      },
                    }}
                    onClick={() => handleDeleteRule(rule.id)}
                  >
                    <TrashSimpleIcon
                      size={20}
                      color={theme.palette.error.main}
                      weight="fill"
                    />
                  </IconButton>
                </Box>

                <Stack spacing={2}>
                  {/* Forward to Number */}
                  <FormControl fullWidth>
                    <FormLabel sx={commonStyles.formLabel}>
                      Forward to Number
                    </FormLabel>
                    <PhoneInput
                      country="in"
                      value={rule.forwardToNumber}
                      onChange={phone =>
                        handleRuleChange(rule.id, 'forwardToNumber', phone)
                      }
                      inputStyle={{
                        width: '100%',
                        height: '40px',
                        fontSize: '14px',
                      }}
                      specialLabel=""
                      containerStyle={{ flex: 1 }}
                      disabled={!rule.enabled}
                    />
                  </FormControl>

                  {/* Timezone */}
                  <FormControl fullWidth>
                    <FormLabel sx={commonStyles.formLabel}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <GlobeHemisphereWestIcon size={14} />
                        Timezone
                      </Box>
                    </FormLabel>
                    <Select
                      value={rule.timezone}
                      onChange={e =>
                        handleRuleChange(rule.id, 'timezone', e.target.value)
                      }
                      sx={commonSelectStyles}
                      disabled={!rule.enabled}
                    >
                      {timeZones.map((timezone: string) => (
                        <MenuItem key={timezone} value={timezone}>
                          {timezone}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Time Range */}
                  <FormControl fullWidth>
                    <FormLabel sx={commonStyles.formLabel}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <ClockIcon size={14} />
                        Time Range
                      </Box>
                    </FormLabel>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <TimePicker
                        value={rule.startTime}
                        onChange={newValue =>
                          handleRuleChange(rule.id, 'startTime', newValue)
                        }
                        disabled={!rule.enabled}
                        ampm={false}
                        sx={{
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            height: '40px',
                            backgroundColor: '#F4F3FF',
                            '& fieldset': {
                              border: 'none',
                            },
                          },
                        }}
                      />
                      <Typography sx={{ color: 'text.secondary' }}>
                        to
                      </Typography>
                      <TimePicker
                        value={rule.endTime}
                        onChange={newValue =>
                          handleRuleChange(rule.id, 'endTime', newValue)
                        }
                        disabled={!rule.enabled}
                        ampm={false}
                        sx={{
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            height: '40px',
                            backgroundColor: '#F4F3FF',
                            '& fieldset': {
                              border: 'none',
                            },
                          },
                        }}
                      />
                    </Box>
                  </FormControl>

                  {/* Secondary Forwarding */}
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1.5,
                      }}
                    >
                      <FormLabel sx={commonStyles.formLabel}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <PhoneIcon size={14} />
                          Secondary Forwarding (Failover)
                        </Box>
                      </FormLabel>
                      <Switch
                        checked={rule.secondaryForwarding.enabled}
                        onChange={e =>
                          handleSecondaryForwardingChange(
                            rule.id,
                            'enabled',
                            e.target.checked
                          )
                        }
                        color="primary"
                        size="small"
                        sx={switchStyles}
                        disabled={!rule.enabled}
                      />
                    </Box>

                    {rule.secondaryForwarding.enabled && (
                      <Stack spacing={2} sx={{ pl: 2 }}>
                        {/* Backup Number */}
                        <FormControl fullWidth>
                          <FormLabel
                            sx={{ ...commonStyles.formLabel, fontSize: '11px' }}
                          >
                            Backup Number
                          </FormLabel>
                          <PhoneInput
                            country="in"
                            value={rule.secondaryForwarding.backupNumber}
                            onChange={phone =>
                              handleSecondaryForwardingChange(
                                rule.id,
                                'backupNumber',
                                phone
                              )
                            }
                            inputStyle={{
                              width: '100%',
                              height: '40px',
                              fontSize: '14px',
                            }}
                            specialLabel=""
                            containerStyle={{ flex: 1 }}
                            disabled={!rule.enabled}
                          />
                        </FormControl>

                        {/* Timeout */}
                        <FormControl fullWidth>
                          <FormLabel
                            sx={{ ...commonStyles.formLabel, fontSize: '11px' }}
                          >
                            Timeout Before Failover (seconds)
                          </FormLabel>
                          <TextField
                            type="number"
                            value={rule.secondaryForwarding.timeoutSeconds}
                            onChange={e =>
                              handleSecondaryForwardingChange(
                                rule.id,
                                'timeoutSeconds',
                                parseInt(e.target.value) || 0
                              )
                            }
                            disabled={!rule.enabled}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '40px',
                                backgroundColor: '#F8F9FC',
                                fontSize: '14px',
                                '& fieldset': {
                                  borderColor: '#E0E0E0',
                                },
                              },
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: '11px',
                              color: 'text.secondary',
                              mt: 0.5,
                            }}
                          >
                            Time to wait before forwarding to backup number
                          </Typography>
                        </FormControl>

                        {/* Conditions */}
                        <Box>
                          <FormLabel
                            sx={{ ...commonStyles.formLabel, fontSize: '11px' }}
                          >
                            Forward to backup when:
                          </FormLabel>
                          <Stack spacing={0.5}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    rule.secondaryForwarding.conditions.noAnswer
                                  }
                                  onChange={e =>
                                    handleConditionChange(
                                      rule.id,
                                      'noAnswer',
                                      e.target.checked
                                    )
                                  }
                                  disabled={!rule.enabled}
                                  size="small"
                                />
                              }
                              label={
                                <Typography sx={{ fontSize: '12px' }}>
                                  No Answer
                                </Typography>
                              }
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    rule.secondaryForwarding.conditions.busy
                                  }
                                  onChange={e =>
                                    handleConditionChange(
                                      rule.id,
                                      'busy',
                                      e.target.checked
                                    )
                                  }
                                  disabled={!rule.enabled}
                                  size="small"
                                />
                              }
                              label={
                                <Typography sx={{ fontSize: '12px' }}>
                                  Busy
                                </Typography>
                              }
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    rule.secondaryForwarding.conditions
                                      .callFailed
                                  }
                                  onChange={e =>
                                    handleConditionChange(
                                      rule.id,
                                      'callFailed',
                                      e.target.checked
                                    )
                                  }
                                  disabled={!rule.enabled}
                                  size="small"
                                />
                              }
                              label={
                                <Typography sx={{ fontSize: '12px' }}>
                                  Call Failed
                                </Typography>
                              }
                            />
                          </Stack>
                        </Box>
                      </Stack>
                    )}
                  </Box>

                  {/* Active Days */}
                  <FormControl fullWidth>
                    <FormLabel sx={commonStyles.formLabel}>
                      Active Days
                    </FormLabel>
                    <Box
                      sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}
                    >
                      {DAYS.map(day => (
                        <Button
                          key={day}
                          variant={
                            rule.activeDays.includes(day)
                              ? 'contained'
                              : 'outlined'
                          }
                          onClick={() => handleDayToggle(rule.id, day)}
                          disabled={!rule.enabled}
                          sx={{
                            minWidth: '52px',
                            height: '36px',
                            fontSize: '13px',
                            fontWeight: 500,
                            borderRadius: '8px',
                            textTransform: 'none',
                            ...(rule.activeDays.includes(day)
                              ? {
                                  backgroundColor: theme.palette.primary.main,
                                  color: '#fff',
                                  '&:hover': {
                                    backgroundColor: theme.palette.primary.main,
                                    opacity: 0.9,
                                  },
                                }
                              : {
                                  borderColor: '#E0E0E0',
                                  color: 'text.secondary',
                                  backgroundColor: '#F8F9FC',
                                  '&:hover': {
                                    backgroundColor: '#E0E0E0',
                                    borderColor: '#E0E0E0',
                                  },
                                }),
                            boxShadow: 'none',
                          }}
                        >
                          {day}
                        </Button>
                      ))}
                    </Box>

                    {/* Quick Select Buttons */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => handleQuickSelectDays(rule.id, 'all')}
                        disabled={!rule.enabled}
                        sx={{
                          fontSize: '12px',
                          textTransform: 'none',
                          color: 'primary.main',
                        }}
                      >
                        All Days
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() =>
                          handleQuickSelectDays(rule.id, 'weekdays')
                        }
                        disabled={!rule.enabled}
                        sx={{
                          fontSize: '12px',
                          textTransform: 'none',
                          color: 'primary.main',
                        }}
                      >
                        Weekdays
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() =>
                          handleQuickSelectDays(rule.id, 'weekend')
                        }
                        disabled={!rule.enabled}
                        sx={{
                          fontSize: '12px',
                          textTransform: 'none',
                          color: 'primary.main',
                        }}
                      >
                        Weekend
                      </Button>
                    </Box>
                  </FormControl>
                </Stack>
              </Box>
            ))}

            {/* Add New Rule Button */}
            <Button
              variant="outlined"
              startIcon={<PlusIcon size={16} />}
              onClick={handleAddRule}
              sx={{
                borderStyle: 'dashed',
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                borderRadius: '8px',
                padding: '12px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                '&:hover': {
                  borderStyle: 'dashed',
                  backgroundColor: 'rgba(90, 76, 230, 0.04)',
                },
              }}
            >
              Add New Time Slot Rule
            </Button>

            {/* Save Button */}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving || isLoading}
              sx={{
                textTransform: 'none',
                py: 1.5,
                px: 3,
                borderRadius: '6px',
                boxShadow: 'none',
                fontSize: '14px',
                fontWeight: 500,
                '&:hover': {
                  boxShadow: 'none',
                },
              }}
            >
              {isSaving ? 'Saving...' : 'Save Call Forwarding Rules'}
            </Button>

            {/* How it works section */}
            <Box
              sx={{
                backgroundColor: '#F8F9FC',
                padding: 2,
                borderRadius: '8px',
                border: '1px solid #E0E0E0',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'custom.textBlue',
                  mb: 1.5,
                }}
              >
                How it works:
              </Typography>
              <Stack spacing={0.5} component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography
                  component="li"
                  sx={{ fontSize: '13px', color: 'text.secondary' }}
                >
                  Rules are evaluated in order from top to bottom
                </Typography>
                <Typography
                  component="li"
                  sx={{ fontSize: '13px', color: 'text.secondary' }}
                >
                  First matching rule forwards the call to the specified number
                </Typography>
                <Typography
                  component="li"
                  sx={{ fontSize: '13px', color: 'text.secondary' }}
                >
                  Time slots use the selected timezone for evaluation
                </Typography>
                <Typography
                  component="li"
                  sx={{ fontSize: '13px', color: 'text.secondary' }}
                >
                  If no rules match, calls forward to the default number
                </Typography>
                <Typography
                  component="li"
                  sx={{ fontSize: '13px', color: 'text.secondary' }}
                >
                  Disabled rules are skipped during evaluation
                </Typography>
              </Stack>
            </Box>
          </Stack>
        )}

        {/* Message Popup */}
        <MessagePopup alert={alert} setAlert={setAlert} />

        {/* Delete Modal */}
        <DeleteModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onDelete={confirmDeleteRule}
          isPending={false}
          title="Delete Forwarding Rule"
          description="Are you sure you want to delete this forwarding rule? This action cannot be undone."
          confirmButtonText="Delete"
          cancelButtonText="Cancel"
        />
      </Box>
    </LocalizationProvider>
  );
};

export default CallForwardingRules;
